import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useVeil } from '../lib/useVeil'
import { maskText, maskName } from '../lib/security'
import type { Person } from '../lib/types'

type Story = Awaited<ReturnType<typeof api.listStories>>['stories'][number]

/** Stories tagged to a person — inline list + quick composer (person panel). */
export default function StoriesSection({ treeId, person, canEdit }: { treeId: string; person: Person; canEdit: boolean }) {
  const [stories, setStories] = useState<Story[]>([])
  const [composing, setComposing] = useState(false)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const veil = useVeil()

  const load = () => api.listStories(treeId, person.id).then(r => setStories(r.stories)).catch(() => {})
  useEffect(() => { load() }, [treeId, person.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!title.trim()) return
    setBusy(true)
    try {
      await api.createStory({ tree_id: treeId, title: title.trim(), body: body.trim(), person_ids: [person.id] })
      setTitle(''); setBody(''); setComposing(false)
      load()
    } finally { setBusy(false) }
  }

  if (stories.length === 0 && !canEdit) return null

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-wider text-slate-600">📖 Stories</p>
        {canEdit && !composing && (
          <button onClick={() => setComposing(true)} className="text-[11px] text-gold-400/80 hover:text-gold-300">+ tell one</button>
        )}
      </div>
      {stories.map(s => (
        <button key={s.id} onClick={() => setExpanded(expanded === s.id ? null : s.id)}
          className="w-full text-left rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2 mb-1.5 hover:border-gold-500/30 transition-colors">
          <p className="text-xs text-slate-200">{s.title}</p>
          <p className="text-[10px] text-slate-600">
            {s.story_date ? `${s.story_date} · ` : ''}by {maskText(s.author_name || 'unknown', veil)}
            {s.persons.length > 1 ? ` · with ${s.persons.filter(p => p.id !== person.id).map(p => maskText(p.name, veil)).join(', ')}` : ''}
          </p>
          {expanded === s.id && s.body && <p className="text-xs text-slate-400 mt-1.5 whitespace-pre-wrap leading-relaxed">{s.body}</p>}
        </button>
      ))}
      {composing && (
        <div className="rounded-xl border border-gold-500/25 bg-gold-500/[0.04] p-2.5 space-y-2">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Story title — e.g. The winter of 1962"
            className="input-dark !py-1.5 text-xs w-full" autoFocus />
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={3}
            placeholder={`What do you remember about ${maskName(person, veil)}?`}
            className="input-dark !py-1.5 text-xs w-full resize-none" />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy || !title.trim()} className="btn-gold !py-1.5 !px-3 text-xs">{busy ? 'Keeping…' : 'Keep this story'}</button>
            <button onClick={() => setComposing(false)} className="btn-ghost !py-1.5 !px-3 text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
